// src/components/ProtectedRoute.tsx

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useUserRole, UserRole } from '../hooks/useUserRole';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'admin' | 'staff' | 'client';
  redirectTo?: string;
}

/**
 * ProtectedRoute component that checks user authentication and role
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredRole,
  redirectTo 
}) => {
  const { user, role, loading } = useUserRole();
  const location = useLocation();

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <div>Loading...</div>
      </div>
    );
  }

  // Redirect to sign-in if not authenticated
  if (!user) {
    return <Navigate to="/sign-in" state={{ from: location }} replace />;
  }

  // If role is required, check if user has it
  if (requiredRole) {
    const hasAccess = checkRoleAccess(role, requiredRole);
    
    if (!hasAccess) {
      // Redirect based on user's actual role
      const redirectPath = getDefaultRoute(role) || redirectTo || '/client-dashboard';
      return <Navigate to={redirectPath} replace />;
    }
  }

  return <>{children}</>;
};

/**
 * Check if user role has access to required role
 */
const checkRoleAccess = (userRole: UserRole, requiredRole: 'admin' | 'staff' | 'client'): boolean => {
  if (!userRole) return false;
  
  // Admin has access to everything
  if (userRole === 'admin') return true;
  
  // Staff can access staff and client routes
  if (userRole === 'staff' && (requiredRole === 'staff' || requiredRole === 'client')) {
    return true;
  }
  
  // Direct role match
  return userRole === requiredRole;
};

/**
 * Get default route based on user role
 */
const getDefaultRoute = (role: UserRole): string | null => {
  switch (role) {
    case 'admin':
      return '/admin-dashboard';
    case 'staff':
      return '/staff-dashboard';
    case 'client':
      return '/client-dashboard';
    default:
      return null;
  }
};

export default ProtectedRoute;

